// src/components/RiskScoreCard.jsx
// Displays client risk score with contributing factors and category breakdown
import React, { useState, useEffect, useRef } from 'react';
import { getChurnPredictionScore, recalculateChurnScore, getRiskHistory } from '../api';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Calendar, Printer, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Clock } from 'lucide-react';
import { RISK_CATEGORIES as CANONICAL_CATEGORIES, getRiskCategory as canonicalGetRiskCategory, CATEGORY_WEIGHTS } from './risk/constants';

// Risk category configuration - derived from canonical constants
// Display-specific styles (color, textColor, lightBg) kept here; thresholds from canonical source
const CARD_STYLES = {
  stable:   { color: 'bg-green-600', textColor: 'text-green-700', lightBg: 'bg-green-100' },
  low:      { color: 'bg-green-500', textColor: 'text-green-600', lightBg: 'bg-green-50' },
  moderate: { color: 'bg-yellow-500', textColor: 'text-yellow-600', lightBg: 'bg-yellow-50' },
  elevated: { color: 'bg-yellow-500', textColor: 'text-yellow-600', lightBg: 'bg-yellow-50' },
  high:     { color: 'bg-orange-500', textColor: 'text-orange-600', lightBg: 'bg-orange-50' },
  critical: { color: 'bg-red-500', textColor: 'text-red-600', lightBg: 'bg-red-50' },
  severe:   { color: 'bg-red-600', textColor: 'text-red-700', lightBg: 'bg-red-100' }
};

const RISK_CATEGORIES = Object.fromEntries(
  CANONICAL_CATEGORIES.map(cat => [cat.key, {
    label: cat.name,
    range: `${cat.min}-${cat.max}`,
    min: cat.min,
    max: cat.max,
    ...CARD_STYLES[cat.key]
  }])
);

// Scoring category weights derived from canonical constants
const CATEGORY_CONFIG = {
  engagement: { weight: Math.round(CATEGORY_WEIGHTS.engagement.weight * 100), label: 'Engagement', color: 'bg-blue-500' },
  utilization: { weight: Math.round(CATEGORY_WEIGHTS.utilization.weight * 100), label: 'Utilization', color: 'bg-purple-500' },
  benefit_fit: { weight: Math.round(CATEGORY_WEIGHTS.benefit_fit.weight * 100), label: 'Benefit Fit', color: 'bg-green-500' },
  life_events: { weight: Math.round(CATEGORY_WEIGHTS.life_events.weight * 100), label: 'Life Events', color: 'bg-orange-500' },
  external: { weight: Math.round(CATEGORY_WEIGHTS.external.weight * 100), label: 'External Risk', color: 'bg-red-500' }
};

export default function RiskScoreCard({ clientId, initialScore, onScoreChange }) {
  const [riskData, setRiskData] = useState(null);
  const [history, setHistory] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [velocityIndicator, setVelocityIndicator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [showFactors, setShowFactors] = useState(false); // Default to collapsed
  const [showHistory, setShowHistory] = useState(false); // Default to collapsed
  const [showBreakdown, setShowBreakdown] = useState(false); // Category breakdown collapsed
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    loadRiskData();
  }, [clientId]);

  // Listen for risk score update events (dispatched by triggers, pulse, etc.)
  useEffect(() => {
    const handleRiskUpdate = (event) => {
      // If event has clientId, only refresh if it matches
      if (!event.detail?.clientId || event.detail.clientId === clientId) {
        loadRiskData();
      }
    };

    // Listen for custom events
    window.addEventListener('risk-score-updated', handleRiskUpdate);

    // Also refresh when page becomes visible (in case score changed while away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadRiskData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('risk-score-updated', handleRiskUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clientId]);

  const loadRiskData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [scoreResult, historyResult] = await Promise.all([
        getChurnPredictionScore(clientId),
        getRiskHistory(clientId, { days: 90 }).catch(() => ({ history: [], velocity: 'stable' }))
      ]);
      console.log('[RiskScoreCard] Score result:', scoreResult);
      setRiskData(scoreResult);
      setHistory(historyResult.history || []);
      setVelocity(historyResult.velocity || 'stable');
      setVelocityIndicator(historyResult.velocityIndicator || null);
      if (onScoreChange && scoreResult.score !== initialScore) {
        onScoreChange(scoreResult.score);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      setError(null);
      console.log('[RiskScoreCard] Recalculating for client:', clientId);
      const result = await recalculateChurnScore(clientId);
      console.log('[RiskScoreCard] Recalculate result:', result);
      setRiskData(result);
      if (onScoreChange) {
        onScoreChange(result.score);
      }
      const historyResult = await getRiskHistory(clientId, { days: 90 }).catch(() => ({ history: [], velocity: 'stable' }));
      setHistory(historyResult.history || []);
      setVelocity(historyResult.velocity || 'stable');
      setVelocityIndicator(historyResult.velocityIndicator || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setRecalculating(false);
    }
  };

  // Helper to print the score history section
  const handlePrint = () => {
    if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Risk Score History</title>
          <style>
            body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; color: #1f2937; }
            .header { border-bottom: 2px solid #172A3A; padding-bottom: 12px; margin-bottom: 20px; }
            .header h1 { margin: 0 0 8px 0; color: #172A3A; font-size: 24px; }
            .header p { margin: 0; color: #6b7280; font-size: 14px; }
            .velocity-badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px; font-weight: 600; font-size: 12px; margin-bottom: 16px; }
            .velocity-spiking { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
            .velocity-rising { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
            .velocity-stable { background: #f9fafb; color: #6b7280; border: 1px solid #e5e7eb; }
            .velocity-improving { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
            .history-item { border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 12px; page-break-inside: avoid; }
            .history-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .history-date { font-weight: 600; color: #374151; }
            .history-score { font-size: 24px; font-weight: 700; }
            .score-change { font-size: 14px; margin-left: 8px; }
            .score-up { color: #dc2626; }
            .score-down { color: #16a34a; }
            .score-same { color: #6b7280; }
            .factors-title { font-weight: 600; color: #374151; font-size: 14px; margin: 12px 0 8px 0; }
            .factor-list { margin: 0; padding-left: 20px; }
            .factor-item { font-size: 13px; color: #4b5563; margin-bottom: 4px; }
            .factor-points { font-weight: 600; color: #dc2626; }
            .category-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
            .cat-stable { background: #dcfce7; color: #166534; }
            .cat-low { background: #dcfce7; color: #166534; }
            .cat-moderate { background: #dbeafe; color: #1e40af; }
            .cat-elevated { background: #fef3c7; color: #b45309; }
            .cat-high { background: #ffedd5; color: #c2410c; }
            .cat-critical { background: #fee2e2; color: #dc2626; }
            .cat-severe { background: #fecaca; color: #991b1b; }
            @media print {
              body { padding: 0; }
              .history-item { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Risk Score History Report</h1>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
          ${printContents}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Get category badge class for print - UNIFIED THRESHOLDS
  const getCategoryClass = (score) => {
    if (score >= 90) return 'cat-severe';
    if (score >= 80) return 'cat-critical';
    if (score >= 65) return 'cat-high';
    if (score >= 50) return 'cat-elevated';
    if (score >= 35) return 'cat-moderate';
    if (score >= 20) return 'cat-low';
    return 'cat-stable';
  };

  // Get category name - UNIFIED THRESHOLDS
  const getCategoryName = (score) => {
    if (score >= 90) return 'Severe';
    if (score >= 80) return 'Critical';
    if (score >= 65) return 'High';
    if (score >= 50) return 'Elevated';
    if (score >= 35) return 'Moderate';
    if (score >= 20) return 'Low';
    return 'Stable';
  };

  // Parse contributing factors
  const parseFactors = (factorsData) => {
    if (!factorsData) return [];
    if (typeof factorsData === 'string') {
      try {
        return JSON.parse(factorsData);
      } catch {
        return [];
      }
    }
    return Array.isArray(factorsData) ? factorsData : [];
  };

  // Get human-readable explanation for score change
  const getChangeExplanation = (currentScore, previousScore, factors, scoreChange) => {
    // Use provided scoreChange, or calculate it
    const change = scoreChange !== null && scoreChange !== undefined
      ? Number(scoreChange)
      : (currentScore - previousScore);

    // Get top factors sorted by points
    const sortedFactors = [...factors].sort((a, b) =>
      (Number(b.points) || Number(b.factor_points) || 0) - (Number(a.points) || Number(a.factor_points) || 0)
    );
    const topFactorNames = sortedFactors
      .slice(0, 3)
      .map(f => f.name || f.factor_name)
      .filter(Boolean);

    // Build explanation based on score change magnitude
    if (Math.abs(change) < 1) {
      if (factors.length > 0) {
        return `Score stable at ${currentScore}. Current risk factors: ${topFactorNames.slice(0, 2).join(', ') || 'monitoring engagement patterns'}.`;
      }
      return `Score stable at ${currentScore}. No significant risk factors detected.`;
    }

    if (change >= 20) {
      return `Score jumped ${change} points (${previousScore} → ${currentScore}). ${topFactorNames.length > 0
        ? `Main drivers: ${topFactorNames.join(', ')}.`
        : 'Multiple high-impact risk factors triggered.'} Immediate attention recommended.`;
    } else if (change >= 10) {
      return `Score increased ${change} points (${previousScore} → ${currentScore}). ${topFactorNames.length > 0
        ? `Contributing factors: ${topFactorNames.join(', ')}.`
        : 'Risk indicators trending upward.'} Schedule a check-in.`;
    } else if (change > 0) {
      return `Score rose ${change} points (${previousScore} → ${currentScore}). ${topFactorNames.length > 0
        ? `Active factors include: ${topFactorNames.join(', ')}.`
        : 'Minor risk increase detected.'} Continue monitoring.`;
    } else if (change <= -20) {
      return `Excellent progress! Score dropped ${Math.abs(change)} points (${previousScore} → ${currentScore}). Risk factors are being successfully addressed.`;
    } else if (change <= -10) {
      return `Good improvement! Score decreased ${Math.abs(change)} points (${previousScore} → ${currentScore}). Engagement efforts are working.`;
    } else {
      return `Score improved by ${Math.abs(change)} points (${previousScore} → ${currentScore}). ${topFactorNames.length > 0
        ? `Remaining factors: ${topFactorNames.join(', ')}.`
        : 'Keep up the engagement.'} Continued monitoring recommended.`;
    }
  };

  // Delegates to canonical getRiskCategory for consistent thresholds
  const getRiskColor = (score) => {
    const cat = getRiskCategory(score);
    return { bg: cat.color, text: cat.textColor, light: cat.lightBg };
  };

  const getRiskCategory = (score) => {
    const cat = canonicalGetRiskCategory(score);
    return RISK_CATEGORIES[cat.key] || RISK_CATEGORIES.stable;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'engagement':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'utilization':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        );
      case 'benefit_fit':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      case 'life_events':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      case 'external':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const score = riskData?.score ?? initialScore ?? 0;
  const colors = getRiskColor(score);
  const category = getRiskCategory(score);
  const factors = riskData?.factors || [];
  const categoryBreakdown = riskData?.categoryBreakdown || {};
  const autoTrigger = riskData?.autoTrigger || null;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${colors.light} rounded-lg flex items-center justify-center`}>
              <svg className={`w-6 h-6 ${colors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Churn Risk Score</h3>
              <p className="text-sm text-gray-500">AI-powered prediction</p>
            </div>
          </div>
          {/* Score is calculated nightly and updated by agent actions */}
          <span className="text-xs text-gray-400">Updated nightly</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Auto-100 Trigger Alert */}
      {autoTrigger && (
        <div className="mx-6 mt-4 p-4 bg-red-100 border border-red-300 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold text-red-800">Auto-100 Trigger Active</span>
          </div>
          <p className="mt-1 text-sm text-red-700">{autoTrigger}</p>
        </div>
      )}

      {/* Score Display */}
      <div className="p-6">
        <div className="flex items-center space-x-6">
          {/* Score Circle */}
          <div className="relative">
            <svg className="w-28 h-28 transform -rotate-90">
              <circle cx="56" cy="56" r="48" stroke="#e5e7eb" strokeWidth="10" fill="none" />
              <circle
                cx="56" cy="56" r="48"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 301.6} 301.6`}
                className={colors.text}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-gray-900">{score}</span>
              <span className="text-xs text-gray-500">/ 100</span>
            </div>
          </div>

          {/* Score Info */}
          <div className="flex-1">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${category.lightBg} ${category.textColor}`}>
              {category.label} Risk
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {score >= 85
                ? 'Immediate action required. This client is at severe risk of churning.'
                : score >= 70
                ? 'High priority. Contact this client within 24 hours.'
                : score >= 55
                ? 'Elevated risk. Schedule a proactive check-in.'
                : score >= 40
                ? 'Monitor regularly and maintain engagement.'
                : 'Client is stable with low churn risk.'}
            </p>
            {riskData?.lastScored && (
              <p className="mt-2 text-xs text-gray-400">
                Last calculated: {new Date(riskData.lastScored).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {/* Category Breakdown - Collapsible */}
        {Object.keys(categoryBreakdown).length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="w-full flex items-center justify-between text-left group"
            >
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                Score Breakdown by Category
                <span className="text-xs text-gray-400 font-normal">
                  ({Object.values(categoryBreakdown).reduce((a, b) => a + b, 0).toFixed(1)} total pts)
                </span>
              </h4>
              {showBreakdown ? (
                <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
              )}
            </button>
            {showBreakdown && (
              <div className="space-y-3 mt-3">
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                  const categoryScore = categoryBreakdown[key] || 0;
                  const maxPoints = config.weight;
                  const percentage = maxPoints > 0 ? (categoryScore / maxPoints) * 100 : 0;

                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{getCategoryIcon(key)}</span>
                          <span className="font-medium text-gray-700">{config.label}</span>
                          <span className="text-xs text-gray-400">({config.weight}% weight)</span>
                        </div>
                        <span className={`font-semibold ${
                          percentage >= 70 ? 'text-red-600' :
                          percentage >= 40 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {categoryScore.toFixed(1)} pts
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            percentage >= 70 ? 'bg-red-500' :
                            percentage >= 40 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, percentage)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* WHY THIS SCORE - Collapsible Section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowFactors(!showFactors)}
            className="w-full flex items-center justify-between text-left group"
          >
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-indigo-600" />
              Why This Score?
              <span className="text-xs text-gray-400 font-normal">
                ({factors.length} factor{factors.length !== 1 ? 's' : ''})
              </span>
            </h4>
            {showFactors ? (
              <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {showFactors && (
            <>
              {/* Score Summary */}
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 mb-4 mt-3">
                {factors.length === 0 ? (
                  <div className="text-center">
                    <p className="text-sm text-gray-700 font-medium">
                      No active risk factors detected.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      This client has a baseline score with no concerning indicators. Recent contact resets many risk factors.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-800">
                      <span className="font-bold">{factors.length} factor{factors.length !== 1 ? 's' : ''}</span> contributing to this score.
                      {(() => {
                        const sortedFactors = [...factors].sort((a, b) => (parseFloat(b.points) || 0) - (parseFloat(a.points) || 0));
                        const topFactor = sortedFactors[0];
                        const topPoints = parseFloat(topFactor?.points) || 0;
                        const totalPoints = factors.reduce((sum, f) => sum + (parseFloat(f.points) || 0), 0);
                        return (
                          <>
                            {' '}Top contributor: <span className="font-semibold text-indigo-700">{topFactor?.name}</span>
                            {topPoints > 0 && <span className="text-gray-500"> (+{topPoints.toFixed(1)} pts)</span>}.
                            <span className="text-gray-600 ml-1">Total factor points: {totalPoints.toFixed(1)}</span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                )}
              </div>

              {/* Detailed Factor List */}
              {factors.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Active Risk Factors (sorted by impact)
                  </div>
                  {factors.sort((a, b) => (parseFloat(b.points) || 0) - (parseFloat(a.points) || 0)).map((factor, idx) => {
                    const pts = parseFloat(factor.points) || 0;
                    return (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        pts >= 15 ? 'bg-red-50 border-red-200' :
                        pts >= 8 ? 'bg-orange-50 border-orange-200' :
                        pts >= 3 ? 'bg-yellow-50 border-yellow-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                          pts >= 15 ? 'bg-red-500' :
                          pts >= 8 ? 'bg-orange-500' :
                          pts >= 3 ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}>
                          #{idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{factor.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs px-2 py-0.5 bg-white rounded border border-gray-200 text-gray-600">
                              {factor.category || 'general'}
                            </span>
                            {factor.value && (
                              <span className="text-xs text-gray-500">{factor.value}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0 ml-3">
                        <span className={`text-lg font-bold ${
                          pts >= 15 ? 'text-red-600' :
                          pts >= 8 ? 'text-orange-600' :
                          pts >= 3 ? 'text-yellow-600' :
                          'text-gray-600'
                        }`}>
                          +{pts.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-400">points</span>
                      </div>
                    </div>
                  )})}

                  {/* Total */}
                  <div className="flex items-center justify-between p-3 bg-indigo-100 rounded-lg border border-indigo-200 mt-3">
                    <span className="text-sm font-bold text-indigo-800">Total Factor Points</span>
                    <span className="text-lg font-bold text-indigo-700">
                      {factors.reduce((sum, f) => sum + (parseFloat(f.points) || 0), 0).toFixed(1)}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mt-2 italic">
                    Note: Final score may differ due to tenure discounts, temporal modifiers, and category weighting.
                  </p>
                </div>
              )}

              {/* Empty state explanation when factors is empty but score > 0 */}
              {factors.length === 0 && score > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                  <p className="font-medium">Score of {score} with no active factors?</p>
                  <p className="text-xs mt-1 text-blue-600">
                    This can happen if:
                    • Recent contact reset engagement factors but other baseline scores remain
                    • Score was manually adjusted
                    • Factors will be updated on next nightly run
                    <br/>
                    Score updates automatically each night and after agent actions.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Enhanced Score History Section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between text-left group"
          >
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              Score History
              <span className="text-xs text-gray-400 font-normal">
                ({history.length} record{history.length !== 1 ? 's' : ''})
              </span>
            </h4>
            {showHistory ? (
              <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {showHistory && (
            <>
              {/* Velocity & Print controls */}
              {history.length > 0 && (
                <div className="flex items-center justify-end gap-2 mt-3 mb-3">
                  {velocity && velocityIndicator && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      velocity === 'spiking' ? 'bg-red-100 text-red-700 border border-red-200' :
                      velocity === 'rising' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                      velocity === 'improving' ? 'bg-green-100 text-green-700 border border-green-200' :
                      'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}>
                      {velocity === 'spiking' && <AlertTriangle className="w-3 h-3" />}
                      {velocity === 'rising' && <TrendingUp className="w-3 h-3" />}
                      {velocity === 'improving' && <TrendingDown className="w-3 h-3" />}
                      {velocity === 'stable' && <Minus className="w-3 h-3" />}
                      {velocityIndicator.label}
                    </span>
                  )}
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Print score history"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    Print
                  </button>
                </div>
              )}

              {/* Visual Timeline Chart */}
              {history.length > 1 && (
                <div className="mb-4 p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">90-Day Trend</span>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span> Stable
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Elevated
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> Critical
                      </span>
                    </div>
                  </div>
                  <div className="flex items-end space-x-1 h-20">
                    {history.slice(0, 30).reverse().map((h, idx) => (
                      <div
                        key={idx}
                        className={`flex-1 rounded-t cursor-pointer transition-all hover:opacity-80 ${
                          h.risk_score >= 90 ? 'bg-red-600' :
                          h.risk_score >= 80 ? 'bg-red-500' :
                          h.risk_score >= 65 ? 'bg-orange-500' :
                          h.risk_score >= 50 ? 'bg-yellow-500' :
                          h.risk_score >= 35 ? 'bg-blue-500' :
                          h.risk_score >= 20 ? 'bg-green-500' :
                          'bg-green-600'
                        }`}
                        style={{ height: `${Math.max(10, (h.risk_score / 100) * 100)}%` }}
                        title={`${h.risk_score} pts - ${new Date(h.scored_at).toLocaleDateString()}`}
                        onClick={() => setSelectedHistoryItem(selectedHistoryItem === idx ? null : idx)}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>Oldest</span>
                    <span>Most Recent</span>
                  </div>
                </div>
              )}

              {/* Detailed History List - Print Friendly */}
              <div ref={printRef} className="space-y-3">
                {/* Print header (hidden on screen, shown in print) */}
                <div className="hidden print:block velocity-badge velocity-{velocity}">
                  7-Day Trend: {velocityIndicator?.label || 'Stable'}
                </div>

                {history.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No score history available yet.</p>
                    <p className="text-xs text-gray-400 mt-1">History will appear after the first recalculation.</p>
                  </div>
                ) : (
                  history.slice(0, 15).map((h, idx) => {
                    const factors = parseFactors(h.contributing_factors);
                    // Use nullish coalescing (??) to properly handle 0 values
                    const calculatedChange = idx < history.length - 1 ? h.risk_score - history[idx + 1].risk_score : 0;
                    const scoreChange = h.score_change ?? calculatedChange;
                    const previousScore = h.previous_score ?? (idx < history.length - 1 ? history[idx + 1].risk_score : h.risk_score);
                    const isExpanded = selectedHistoryItem === idx;

                    return (
                      <div
                        key={idx}
                        className={`history-item border rounded-xl overflow-hidden transition-all ${
                          isExpanded ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {/* History Item Header - Always Visible */}
                        <button
                          onClick={() => setSelectedHistoryItem(isExpanded ? null : idx)}
                          className="w-full p-4 text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  {new Date(h.scored_at).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                                <span className="text-xs text-gray-500 ml-6">
                                  {new Date(h.scored_at).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                  {h.scoring_method && ` • ${h.scoring_method === 'full_calculation' ? 'Full Analysis' : h.scoring_method}`}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              {/* Score with category badge */}
                              <div className="text-right">
                                <div className="flex items-center gap-2">
                                  <span className={`text-2xl font-bold ${getRiskColor(h.risk_score).text}`}>
                                    {h.risk_score}
                                  </span>
                                  <span className={`category-badge px-2 py-0.5 rounded text-xs font-semibold ${
                                    h.risk_score >= 90 ? 'bg-red-100 text-red-700' :
                                    h.risk_score >= 80 ? 'bg-red-100 text-red-600' :
                                    h.risk_score >= 65 ? 'bg-orange-100 text-orange-700' :
                                    h.risk_score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                    h.risk_score >= 35 ? 'bg-blue-100 text-blue-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {getCategoryName(h.risk_score)}
                                  </span>
                                </div>

                                {/* Score Change Indicator */}
                                {scoreChange !== 0 && (
                                  <div className={`flex items-center justify-end gap-1 mt-0.5 text-sm font-medium ${
                                    scoreChange > 0 ? 'text-red-600' : 'text-green-600'
                                  }`}>
                                    {scoreChange > 0 ? (
                                      <ArrowUpRight className="w-4 h-4" />
                                    ) : (
                                      <ArrowDownRight className="w-4 h-4" />
                                    )}
                                    <span>{scoreChange > 0 ? '+' : ''}{scoreChange} pts</span>
                                  </div>
                                )}
                                {scoreChange === 0 && idx < history.length - 1 && (
                                  <div className="flex items-center justify-end gap-1 mt-0.5 text-xs text-gray-400">
                                    <Minus className="w-3 h-3" />
                                    <span>No change</span>
                                  </div>
                                )}
                              </div>

                              {/* Expand Icon */}
                              <div className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50/50">
                            {/* Explanation */}
                            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-100">
                              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                What Changed
                              </h5>
                              <p className="text-sm text-gray-700">
                                {getChangeExplanation(h.risk_score, previousScore, factors, scoreChange)}
                              </p>
                            </div>

                            {/* Contributing Factors */}
                            {factors.length > 0 && (
                              <div className="mt-3">
                                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                  Active Risk Factors ({factors.length})
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {factors.slice(0, 6).map((factor, fIdx) => (
                                    <div
                                      key={fIdx}
                                      className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100"
                                    >
                                      <span className="text-sm text-gray-700 truncate flex-1">
                                        {factor.name || factor.factor_name || 'Unknown factor'}
                                      </span>
                                      <span className={`text-xs font-semibold ml-2 ${
                                        (factor.points || factor.factor_points || 0) >= 10 ? 'text-red-600' :
                                        (factor.points || factor.factor_points || 0) >= 5 ? 'text-orange-600' :
                                        'text-yellow-600'
                                      }`}>
                                        +{(factor.points || factor.factor_points || 0).toFixed(1)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                {factors.length > 6 && (
                                  <p className="text-xs text-gray-500 mt-2 text-center">
                                    +{factors.length - 6} more factors
                                  </p>
                                )}
                              </div>
                            )}

                            {factors.length === 0 && (
                              <div className="mt-3 text-sm text-gray-500 italic">
                                No detailed factor breakdown available for this record.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Print-only detailed view (always shows factors) */}
                        <div className="hidden print:block px-4 pb-4 border-t border-gray-200">
                          <p className="factors-title">Score Change Explanation:</p>
                          <p className="text-sm text-gray-600 mb-2">
                            {getChangeExplanation(h.risk_score, previousScore, factors, scoreChange)}
                          </p>
                          {factors.length > 0 && (
                            <>
                              <p className="factors-title">Contributing Factors:</p>
                              <ul className="factor-list">
                                {factors.slice(0, 5).map((factor, fIdx) => (
                                  <li key={fIdx} className="factor-item">
                                    {factor.name || factor.factor_name}: <span className="factor-points">+{(factor.points || factor.factor_points || 0).toFixed(1)} pts</span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}

                {history.length > 15 && (
                  <p className="text-center text-sm text-gray-500 py-2">
                    Showing 15 of {history.length} records. Print for full history.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
